// LeadMatchingPanel.tsx - AI-powered lead matching interface
import React, { useState } from 'react';
import {
  Card,
  Text,
  Button,
  Stack,
  Group,
  Badge,
  Progress,
  Divider,
  Alert,
  LoadingOverlay,
  ActionIcon,
  Tooltip,
  NumberInput,
  Switch,
  Image,
} from '@mantine/core';
import {
  IconRefresh,
  IconInfoCircle,
  IconCar,
  IconCheck,
  IconExternalLink,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';

interface Lead {
  id: string;
  customerName: string;
  email: string;
  phone?: string;
  carInterest: string;
  budget?: number;
  status: string;
}

interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  price: number;
  mileage: number;
  fuelType: string;
  transmission: string;
  color: string;
  images?: string[];
  location?: string;
  status: string;
}

interface Match {
  vehicle: Vehicle;
  score: number;
  reasons: string[];
  priceMatch: boolean;
  brandMatch: boolean;
  budgetDiff?: number;
}

interface LeadMatchingPanelProps {
  lead: Lead;
  onMatchSelected?: (match: Match) => void;
}

export function LeadMatchingPanel({ lead, onMatchSelected }: LeadMatchingPanelProps) {
  const [minScore, setMinScore] = useState(60);
  const [autoScore, setAutoScore] = useState(true);

  // Fetch matches
  const {
    data: matchesData,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ['lead-matches', lead.id, minScore],
    queryFn: async () => {
      const response = await fetch(`/api/leads/${lead.id}/match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          criteria: {
            minMatchScore: minScore,
            autoScore,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch matches');
      }

      return response.json();
    },
    enabled: !!lead.id,
  });

  const matches: Match[] = matchesData?.data?.matches || [];
  const bestMatch: Match | null = matchesData?.data?.bestMatch || null;

  // Refresh matches
  const handleRefresh = () => {
    refetch();
    notifications.show({
      title: 'Refreshing Matches',
      message: 'Finding the best vehicles for this lead...',
      color: 'blue',
    });
  };

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'orange';
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <LoadingOverlay visible={isLoading} />

      {/* Header */}
      <Group justify="space-between" mb="md">
        <div>
          <Text size="lg" fw={700}>
            AI Vehicle Matching
          </Text>
          <Text size="sm" c="dimmed">
            Find the perfect vehicle for {lead.customerName}
          </Text>
        </div>
        <Group>
          <Tooltip label="Refresh matches">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Matching Criteria */}
      <Card withBorder padding="sm" mb="md" bg="gray.0">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Minimum Match Score
            </Text>
            <NumberInput
              value={minScore}
              onChange={(val) => setMinScore(Number(val))}
              min={0}
              max={100}
              step={5}
              w={100}
              size="xs"
            />
          </Group>
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Auto-score leads
            </Text>
            <Switch checked={autoScore} onChange={(e) => setAutoScore(e.currentTarget.checked)} />
          </Group>
        </Stack>
      </Card>

      {/* Lead Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" mb="md">
        <Text size="sm">
          <strong>Looking for:</strong> {lead.carInterest}
        </Text>
        {lead.budget && (
          <Text size="sm">
            <strong>Budget:</strong> {formatCurrency(lead.budget)}
          </Text>
        )}
      </Alert>

      {/* Error State */}
      {error && (
        <Alert color="red" mb="md">
          Failed to load matches. Please try again.
        </Alert>
      )}

      {/* Best Match */}
      {bestMatch && (
        <Card withBorder padding="md" mb="md" bg="green.0">
          <Group justify="space-between" mb="xs">
            <Badge color="green" size="lg" leftSection={<IconCheck size={14} />}>
              Best Match ({bestMatch.score}%)
            </Badge>
          </Group>
          <VehicleMatchCard match={bestMatch} onSelect={onMatchSelected} featured />
        </Card>
      )}

      <Divider my="md" label="All Matches" labelPosition="center" />

      {/* Matches List */}
      {matches.length === 0 && !isLoading && (
        <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
          No matches found. Try lowering the minimum match score or add more vehicles to your
          inventory.
        </Alert>
      )}

      <Stack gap="md">
        {matches.map((match) => (
          <VehicleMatchCard key={match.vehicle.id} match={match} onSelect={onMatchSelected} />
        ))}
      </Stack>
    </Card>
  );
}

interface VehicleMatchCardProps {
  match: Match;
  onSelect?: (match: Match) => void;
  featured?: boolean;
}

function VehicleMatchCard({ match, onSelect, featured = false }: VehicleMatchCardProps) {
  const { vehicle, score, reasons, priceMatch, brandMatch, budgetDiff } = match;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'orange';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <Card withBorder padding="md" radius="md">
      <Group align="flex-start" wrap="nowrap">
        {/* Vehicle Image */}
        {vehicle.images && vehicle.images[0] ? (
          <Image
            src={vehicle.images[0]}
            alt={`${vehicle.make} ${vehicle.model}`}
            width={120}
            height={90}
            radius="md"
            fit="cover"
          />
        ) : (
          <Card withBorder w={120} h={90} bg="gray.1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconCar size={40} color="gray" />
          </Card>
        )}

        {/* Vehicle Info */}
        <Stack gap="xs" style={{ flex: 1 }}>
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                {vehicle.year} {vehicle.make} {vehicle.model}
              </Text>
              <Text size="sm" c="dimmed">
                {vehicle.mileage.toLocaleString()} km • {vehicle.fuelType} • {vehicle.transmission}
              </Text>
            </div>
            <Badge color={getScoreColor(score)} size="xl" variant="filled">
              {score}% Match
            </Badge>
          </Group>

          {/* Match Progress */}
          <Progress value={score} color={getScoreColor(score)} size="sm" radius="xl" />

          {/* Match Reasons */}
          <Group gap="xs">
            {brandMatch && (
              <Badge color="blue" variant="light" size="sm">
                Brand Match
              </Badge>
            )}
            {priceMatch && (
              <Badge color="green" variant="light" size="sm">
                Price Match
              </Badge>
            )}
            {vehicle.status === 'available' && (
              <Badge color="teal" variant="light" size="sm">
                Available
              </Badge>
            )}
          </Group>

          {/* Reasons */}
          {reasons.length > 0 && (
            <Text size="xs" c="dimmed">
              {reasons.slice(0, 2).join(' • ')}
            </Text>
          )}

          {/* Price and Budget */}
          <Group justify="space-between">
            <div>
              <Text size="xl" fw={700} c="blue">
                {formatCurrency(vehicle.price)}
              </Text>
              {budgetDiff !== undefined && budgetDiff !== 0 && (
                <Text size="xs" c={budgetDiff > 0 ? 'red' : 'green'}>
                  {budgetDiff > 0 ? '+' : ''}
                  {formatCurrency(budgetDiff)} vs budget
                </Text>
              )}
            </div>
            <Group gap="xs">
              <Tooltip label="View vehicle details">
                <ActionIcon variant="light" color="blue" component="a" href={`/inventory/${vehicle.id}`} target="_blank">
                  <IconExternalLink size={18} />
                </ActionIcon>
              </Tooltip>
              {onSelect && (
                <Button
                  variant={featured ? 'filled' : 'light'}
                  color={featured ? 'green' : 'blue'}
                  size="sm"
                  onClick={() => onSelect(match)}
                >
                  Select Match
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      </Group>
    </Card>
  );
}
